/**
 * Settings API Service — Product Portal
 *
 * IDP Federation endpoints via tms-api-onboarding.
 * - IDP CRUD (POST/PUT): tenant_id is merged into the request body.
 * - IDP CRUD (GET/DELETE): tenant_id is sent as a query param.
 * - Tenant-config endpoints (e.g. fetchSpDetails): tenant_id is NOT sent;
 *   the backend resolves the tenant from the domain query param instead.
 */
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import { useAuthStore } from '@/stores/auth.store';
import { CONTROL_PLANE_TENANT_ID } from '@/modules/users/constants';
import type { PermissionModule, RolePermissionEntry } from '../types';

const api = getApiClient();

// ---------------------------------------------------------------------------
// Protocol / Provider dropdown options
// ---------------------------------------------------------------------------

export const IDP_PROTOCOL_OPTIONS = [
  { label: 'SAML 2.0', value: 'saml' },
  { label: 'OIDC / OAuth 2.0', value: 'oidc' },
];

export const IDP_PROVIDER_OPTIONS = [
  { label: 'Microsoft Azure AD / Entra ID', value: 'azure' },
  { label: 'Google Workspace', value: 'google' },
  { label: 'Okta', value: 'okta' },
  { label: 'Auth0', value: 'auth0' },
  { label: 'AWS IAM Identity Center', value: 'aws-sso' },
  { label: 'GCP Cloud Identity', value: 'gcp' },
  { label: 'Custom / Other', value: 'custom' },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CreateIdpPayload = {
  provider_name: string;
  provider_type: 'SAML' | 'OIDC';
  provider_details: Record<string, string>;
  attribute_mapping?: Record<string, string>;
  idp_identifiers?: string[];
};

export type IdpProviderResponse = {
  provider_name: string;
  provider_type: 'SAML' | 'OIDC';
  provider_details: Record<string, string>;
  attribute_mapping: Record<string, string>;
  idp_identifiers: string[];
  creation_date: string;
  last_modified_date: string;
};

export type UpdateIdpPayload = {
  provider_type: 'SAML' | 'OIDC';
  provider_details?: Record<string, string>;
  attribute_mapping?: Record<string, string>;
  idp_identifiers?: string[];
};

export type SpRequiredAttribute = {
  name: string;
  required: boolean;
  description: string;
};

export type SpDetailsResponse = {
  acs_url: string;
  sp_entity_id: string;
  audience_uri: string;
  audience_restriction: string;
  sp_metadata_url: string;
  cognito_domain: string;
  region: string;
  required_attributes: SpRequiredAttribute[];
};

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/** Resolves tenant_id from the auth store, throwing if unavailable. */
function getTenantId(): string {
  const { tenantId } = useAuthStore();
  if (!tenantId) {
    throw new Error('tenant_id is not available — ensure the user is authenticated before calling IDP APIs');
  }
  return tenantId;
}

/**
 * Create a SAML or OIDC identity provider.
 * POST /onboarding/api/v1/idps
 */
export async function createIdp(payload: CreateIdpPayload): Promise<IdpProviderResponse> {
  const tenantId = getTenantId();
  const response = await api.post<IdpProviderResponse>(
    `${API_ROUTES.ONBOARDING}/idps`,
    { ...payload, tenant_id: tenantId },
  );
  return response.data;
}

/**
 * List all identity providers.
 * GET /onboarding/api/v1/idps
 */
export async function listIdps(): Promise<IdpProviderResponse[]> {
  const tenantId = getTenantId();
  const response = await api.get<{ providers: IdpProviderResponse[] }>(
    `${API_ROUTES.ONBOARDING}/idps`,
    { params: { tenant_id: tenantId } },
  );
  return response.data.providers;
}

/**
 * Get a single identity provider by name.
 * GET /onboarding/api/v1/idps/:provider_name
 */
export async function getIdp(providerName: string): Promise<IdpProviderResponse> {
  const tenantId = getTenantId();
  const response = await api.get<IdpProviderResponse>(
    `${API_ROUTES.ONBOARDING}/idps/${encodeURIComponent(providerName)}`,
    { params: { tenant_id: tenantId } },
  );
  return response.data;
}

/**
 * Update an existing identity provider.
 * PUT /onboarding/api/v1/idps/:provider_name
 */
export async function updateIdp(
  providerName: string,
  payload: UpdateIdpPayload,
): Promise<IdpProviderResponse> {
  const tenantId = getTenantId();
  const response = await api.put<IdpProviderResponse>(
    `${API_ROUTES.ONBOARDING}/idps/${encodeURIComponent(providerName)}`,
    { ...payload, tenant_id: tenantId },
  );
  return response.data;
}

/**
 * Delete an identity provider.
 * DELETE /onboarding/api/v1/idps/:provider_name
 */
export async function deleteIdp(providerName: string): Promise<void> {
  const tenantId = getTenantId();
  await api.delete(
    `${API_ROUTES.ONBOARDING}/idps/${encodeURIComponent(providerName)}`,
    { params: { tenant_id: tenantId } },
  );
}

/**
 * Fetch Cognito SP (Service Provider) details for IDP configuration.
 * GET /onboarding/api/v1/tenant-config/sp-details?domain=<hostname>
 */
export async function fetchSpDetails(): Promise<SpDetailsResponse> {
  const domain = window.location.hostname;
  const response = await api.get<SpDetailsResponse>(
    `${API_ROUTES.ONBOARDING}/tenant-config/sp-details`,
    { params: { domain } },
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Roles & Permissions — role-module-matrix via tms-api-onboarding
// ---------------------------------------------------------------------------

type ApiRoleKey =
  | 'ADMIN'
  | 'PRODUCTION_SUPPORT'
  | 'PRODUCT_TEAM'
  | 'DEFAULT';

type ApiModuleKey =
  | 'PRODUCT_MANAGEMENT'
  | 'TENANT_MANAGEMENT'
  | 'USER_MANAGEMENT'
  | 'ENTERPRISES_MANAGEMENT'
  | 'SETTINGS'
  | 'RECALL'
  | 'AUDIT_LOG';

type RoleModuleMatrixRow = Record<ApiModuleKey, boolean>;
type RoleModuleMatrix = Record<ApiRoleKey, RoleModuleMatrixRow>;

type RoleModuleMatrixApiResponse = {
  hospital_id: string;
  matrix: RoleModuleMatrix;
  source: 'hospital' | 'default';
  updated_by?: string;
  updated_at?: string;
};

const ROLE_META: Record<ApiRoleKey, { roleName: string; roleSubtitle: string }> = {
  ADMIN:              { roleName: 'Admin',              roleSubtitle: 'Full Access'                },
  PRODUCTION_SUPPORT: { roleName: 'Production Support', roleSubtitle: 'Production Operations'      },
  PRODUCT_TEAM:       { roleName: 'Product Team',       roleSubtitle: 'Product Management'         },
  DEFAULT:            { roleName: 'Default',            roleSubtitle: 'Baseline Access'            },
};

const API_ROLE_ORDER: ApiRoleKey[] = [
  'ADMIN',
  'PRODUCTION_SUPPORT',
  'PRODUCT_TEAM',
  'DEFAULT',
];

function toUiPermissions(row: RoleModuleMatrixRow): RolePermissionEntry['permissions'] {
  return {
    productManagement:    row.PRODUCT_MANAGEMENT,
    tenantManagement:     row.TENANT_MANAGEMENT,
    userManagement:       row.USER_MANAGEMENT,
    enterprisesManagement: row.ENTERPRISES_MANAGEMENT,
    settings:             row.SETTINGS,
    recall:               row.RECALL,
    auditLogs:            row.AUDIT_LOG,
  };
}

function toApiPermissions(p: RolePermissionEntry['permissions']): RoleModuleMatrixRow {
  return {
    PRODUCT_MANAGEMENT:    p.productManagement,
    TENANT_MANAGEMENT:     p.tenantManagement,
    USER_MANAGEMENT:       p.userManagement,
    ENTERPRISES_MANAGEMENT: p.enterprisesManagement,
    SETTINGS:              p.settings,
    RECALL:                p.recall,
    AUDIT_LOG:             p.auditLogs,
  };
}

/**
 * Fetch the role-module permission matrix for the control-plane tenant.
 * GET /onboarding/api/v1/tenants/{CONTROL_PLANE_TENANT_ID}/role-module-matrix/?apptype=CONTROLPLANE
 */
export async function fetchRolesPermissions(): Promise<RolePermissionEntry[]> {
  const url = `${API_ROUTES.ONBOARDING}/tenants/${encodeURIComponent(CONTROL_PLANE_TENANT_ID)}/role-module-matrix/`;
  const response = await api.get<RoleModuleMatrixApiResponse>(
    url,
    { params: { apptype: 'CONTROLPLANE' } },
  );
  const { matrix } = response.data;
  return API_ROLE_ORDER
    .filter((roleKey) => matrix[roleKey] !== undefined)
    .map((roleKey) => ({
      id: roleKey,
      ...ROLE_META[roleKey],
      permissions: toUiPermissions(matrix[roleKey]),
    }));
}

/**
 * Save the role-module permission matrix for the control-plane tenant.
 * PUT /onboarding/api/v1/tenants/{tenantId}/role-module-matrix/?apptype=CONTROLPLANE
 */
export async function saveRolesPermissions(roles: RolePermissionEntry[]): Promise<void> {
  const { staffId } = useAuthStore();
  const matrix = Object.fromEntries(
    roles.map((r) => [r.id as ApiRoleKey, toApiPermissions(r.permissions)]),
  ) as RoleModuleMatrix;
  await api.put(
    `${API_ROUTES.ONBOARDING}/tenants/${encodeURIComponent(CONTROL_PLANE_TENANT_ID)}/role-module-matrix/`,
    { matrix, updated_by: staffId ?? CONTROL_PLANE_TENANT_ID },
    { params: { apptype: 'CONTROLPLANE' } },
  );
}

// Re-export types needed by other modules
export type { PermissionModule, RolePermissionEntry };
